import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  pendingOutcome,
  type CallAttempt,
  type CallAttemptOutcome,
  type Order,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { useCurrentUser } from "@/auth/queries";
import { reportingNow } from "@/lib/reportClock";

export const callKeys = {
  order: (orderId: string) => ["orders", "calls", orderId] as const,
};

/**
 * Whether an order's calls are confirmation calls (#636): a COD order still
 * waiting for one. Calls on other orders are not logged; delivery follow-ups
 * are out of scope for now.
 */
export function awaitsConfirmationCall(
  order: Pick<Order, "stage" | "cancelledAt">,
): boolean {
  return order.stage === "new" && order.cancelledAt === null;
}

/**
 * One order's call log (#636): the attempts, newest first, the one to ask
 * "How did it go?" about, and the two writes. `enabled` is false for an order
 * that is not awaiting confirmation, so nothing is read or written for it.
 */
export function useOrderCalls(orderId: string, enabled: boolean) {
  const data = useDataSource();
  const client = useQueryClient();
  const me = useCurrentUser();

  const query = useQuery({
    queryKey: callKeys.order(orderId),
    queryFn: () => data.orders.callAttempts(orderId),
    enabled,
  });
  const attempts: CallAttempt[] = query.data ?? [];

  const refresh = () =>
    client.invalidateQueries({ queryKey: callKeys.order(orderId) });

  const record = useMutation({
    mutationFn: (contactId: string | null) =>
      data.orders.recordCallAttempt(orderId, { contactId }),
    onSettled: refresh,
  });

  const outcome = useMutation({
    mutationFn: (input: { attemptId: string; outcome: CallAttemptOutcome }) =>
      data.orders.setCallOutcome(orderId, input.attemptId, input.outcome),
    onSettled: refresh,
  });

  const pending =
    enabled && me.data
      ? pendingOutcome(attempts, me.data.id, reportingNow())
      : null;

  return { attempts, pending, record, outcome, enabled };
}
